// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCache } from '@shared/commons/DataCache';
import { DynProperty } from '@shared/commons/DynProperty';
import { Resources } from '@shared/localizations/Resources';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { WatchlistEnum } from '@shared/utils/Enums/WatchlistEnum';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { InstrumentDescriptionUtils } from '@shared/utils/Instruments/InstrumentDescriptionUtils';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { MathUtils } from '@shared/utils/MathUtils';
import { QuoteValid } from '@shared/utils/Quotes/QuoteValid';
import { WATCHLIST_COLUMN_RULE } from '@shared/utils/Rules/WatchlistRules';
import { StringUtils } from '@shared/utils/StringUtils';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { VolumeFormatter } from '@shared/utils/Volume/VolumeFormatter';
import { BaseItem, ColumnData } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
import { QuickTableEditingInfo } from '../elements/QuickTable/QuickTableMisc';
import { ThemeManager } from '../misc/ThemeManager';
import { WDSettings } from '../settings/WDGeneralSettings';
export class BaseInformer extends BaseItem {
    get isDisplayed() {
        return super.isDisplayed;
    }
    set isDisplayed(value) {
        const isChangedDisaplyedState = this.isDisplayed !== value;
        super.isDisplayed = value;
        if (isChangedDisaplyedState) {
            this.sendSubscription(this.isDisplayed);
        }
    }
    constructor(dataCache, sessionSettings) {
        super(sessionSettings);
        this.customColumnValues = {};
        this.customColumnFormattedValues = {};
        this.instrument = null;
        this.ContractSize = 0;
        this.Comment = '';
        this.dataCache = dataCache;
        this.numId = (BaseInformer.num++).toString();
    }
    getQuickTableCellForeground(column) {
        if (column === WatchlistEnum.INSTRUMENT_COL_INDEX) {
            return ThemeManager.colors.tableSymbolForeground;
        }
        else {
            return super.getQuickTableCellForeground(column);
        }
    }
    getInstrumentDescription() {
        return this.instrument.getInstrumentDescription();
    }
    InstrumentName() {
        return this.instrument.GetInteriorID();
    }
    ColumnCount() {
        return BaseInformer.columnsParams.length;
    }
    getColumnValue(column) {
        const customColumnValues = this.customColumnValues;
        if (column >= BaseInformer.columnsParams.length &&
            Object.prototype.hasOwnProperty.call(customColumnValues, column)) {
            return customColumnValues[column];
        }
        const instrument = this.instrument;
        const Level1 = instrument.Level1;
        const insDayInfo = this.instrument.InstrumentDayInfo;
        const ShowLots = WDSettings.isQuantityInLots;
        const account = this.GetCurrentAccount();
        if (!Level1) {
            return 0;
        }
        switch (column) {
            case WatchlistEnum.INSTRUMENT_COL_INDEX:
                return instrument.DisplayName();
            case WatchlistEnum.LAST_COL_INDEX:
                return Level1.GetLastPrice(account);
            case WatchlistEnum.LASTSIZE_COL_INDEX:
                return Level1.GetLastSize(ShowLots);
            case WatchlistEnum.DATE_COL_INDEX:
            case WatchlistEnum.TIME_COL_INDEX: {
                const lastMessageTime = Level1.getLastMessageTime;
                if (isNullOrUndefined(lastMessageTime))
                    return null;
                if (lastMessageTime.getTime() === 0)
                    return null;
                return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(lastMessageTime);
            }
            case WatchlistEnum.BID_COL_INDEX:
                return Level1.GetBid(account);
            case WatchlistEnum.BIDSIZE_COL_INDEX:
                return Level1.getBidSize(ShowLots);
            case WatchlistEnum.ASK_COL_INDEX:
                return Level1.GetAsk(account);
            case WatchlistEnum.ASKSIZE_COL_INDEX:
                return Level1.getAskSize(ShowLots);
            case WatchlistEnum.CHANGE_COL_INDEX:
                return Level1.GetChange(account);
            case WatchlistEnum.CHANGEPR_COL_INDEX:
                return Level1.GetChangePercent(account);
            case WatchlistEnum.OPEN_COL_INDEX:
                return insDayInfo.getOpen(account);
            case WatchlistEnum.HIGH_COL_INDEX:
                return insDayInfo.getHigh(account);
            case WatchlistEnum.LOW_COL_INDEX:
                return insDayInfo.getLow(account);
            case WatchlistEnum.PREVCLOSE_COL_INDEX:
                return insDayInfo.getPrevClose(account);
            case WatchlistEnum.CONTRACTSIZE_COL_INDEX:
                return instrument.LotSize;
            case WatchlistEnum.EXPDATE_COL_INDEX:
                if (instrument !== null && instrument.ExpDateReal.getFullYear() > 2000) {
                    return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(instrument.ExpDateReal);
                }
                else {
                    return DateTimeUtils._ZeroTime;
                }
            case WatchlistEnum.SPREAD_COL_INDEX:
                return Level1.GetSpread(account);
            case WatchlistEnum.SPREADPR_COL_INDEX:
                return Level1.GetSpreadPercent(account);
            case WatchlistEnum.DESCR_COL_INDEX:
                return instrument.DescriptionValue();
            case WatchlistEnum.TYPE_COL_INDEX:
                return instrument.InstrType;
            case WatchlistEnum.MARKETCAP_COL_INDEX:
                return 0;
            case WatchlistEnum.NOQUOTES_COL_INDEX:
                return Level1.GetLastUpdate();
            case WatchlistEnum.INDICATIVEAUCTIONPRICE_COL_INDEX:
                return Level1.GetIndicativeAuctionPrice();
            case WatchlistEnum.CURRENTSESSION_COL_INDEX:
                return instrument.ExchangeSessionName;
            case WatchlistEnum.TRADINGSTATUS_COL_INDEX:
                return InstrumentUtils.GetTradingStatus(instrument);
            case WatchlistEnum.TICKS_COL_INDEX:
                return Level1.GetTicks();
            case WatchlistEnum.OPENINTEREST_COL_INDEX:
                return instrument.OpenInterest > 0 ? instrument.OpenInterest : 0;
            case WatchlistEnum.SETTLEMENTPRICE_COL_INDEX:
                return instrument.SettlementPrice;
            case WatchlistEnum.PREVSETTLEMENTPRICE_COL_INDEX:
                return instrument.PrevSettlementPrice;
            case WatchlistEnum.CLOSE_COL_INDEX:
                return insDayInfo.getClose(account);
            case WatchlistEnum.EXCHANGEMARKETDATA_COL_INDEX:
                return instrument != null ? instrument.MarketDataExchange : Resources.getResource('ExchangeId.None');
            case WatchlistEnum.VOLUME_COL_INDEX:
                return insDayInfo.TotalVolume();
            case WatchlistEnum.MATCHVOLUME_COL_INDEX:
                return Level1.GetIndicativeAuctionVolume(ShowLots);
            case WatchlistEnum.COMMENT_COL_INDEX:
                return this.Comment || '';
            case WatchlistEnum.HIGH13_COL_INDEX:
                return instrument.High13Week === null ? NaN : instrument.High13Week;
            case WatchlistEnum.LOW13_COL_INDEX:
                return instrument.Low13Week === null ? NaN : instrument.Low13Week;
            case WatchlistEnum.HIGH26_COL_INDEX:
                return instrument.High26Week === null ? NaN : instrument.High26Week;
            case WatchlistEnum.LOW26_COL_INDEX:
                return instrument.Low26Week === null ? NaN : instrument.Low26Week;
            case WatchlistEnum.HIGH52_COL_INDEX:
                return instrument.FiftyTwoWeekHighPrice === null ? NaN : instrument.FiftyTwoWeekHighPrice;
            case WatchlistEnum.LOW52_COL_INDEX:
                return instrument.FiftyTwoWeekLowPrice === null ? NaN : instrument.FiftyTwoWeekLowPrice;
            case WatchlistEnum.REFERENCEPRICE_COL_INDEX:
                return MathUtils.ProcessNaN(Level1.getReferencePrice);
            case WatchlistEnum.TRADEDVALUE_COL_INDEX:
                return MathUtils.ProcessNaN(Level1.getTradedValue);
            case WatchlistEnum.OFFEXCHANGEVOLUME_COL_INDEX:
                return MathUtils.ProcessNaN(Level1.getOffExchangeVolume(ShowLots));
            case WatchlistEnum.OFFEXCHANGEVALUE_COL_INDEX:
                return MathUtils.ProcessNaN(Level1.getOffExchangeValue);
            case WatchlistEnum.NORMALMARKETSIZE_COL_INDEX:
                return Level1.getNormalMarketSize;
            case WatchlistEnum.LIMITHIGH_COL_INDEX:
                return MathUtils.ProcessNaN(instrument.Limits.GetHighLimitPriceByMeasure());
            case WatchlistEnum.LIMITLOW_COL_INDEX:
                return MathUtils.ProcessNaN(instrument.Limits.GetLowLimitPriceByMeasure());
            case WatchlistEnum.CBAUCTIONSTART_COL_INDEX:
                return Level1.CbAuctionStart;
            case WatchlistEnum.AUCTIONENDTIME_COL_INDEX:
                return Level1.strAuctionEndTime;
            case WatchlistEnum.REMAININGQTY_COL_INDEX:
                return Level1.strRemainingQty;
            case WatchlistEnum.REMAININGQTYSIDE_COL_INDEX:
                return Level1.strRemainingQtySide;
            case WatchlistEnum.ORDER_COL_INDEX:
                return instrument.GetInterTraderButtonStatus(false);
            case WatchlistEnum.TRADE_COL_INDEX:
                return instrument.GetInterTraderButtonStatus(true);
            case WatchlistEnum.AVGTRADEDPRICE_COL_INDEX:
                return Level1.GetAvgTradedPrice();
            case WatchlistEnum.TOTALBUYQUANTITY_COL_INDEX:
                return Level1.GetTotalBuyQty();
            case WatchlistEnum.TOTALSELLQUANTITY_COL_INDEX:
                return Level1.GetTotalSellQty();
            case WatchlistEnum.HIGHOPENINTEREST_COL_INDEX:
                return Level1.GetHighOpenInterest();
            case WatchlistEnum.LOWOPENINTEREST_COL_INDEX:
                return Level1.GetLowOpenInterest();
            case WatchlistEnum.INITIATORPRICE_COL_INDEX:
                return Level1.GetInitiatorPrice();
            case WatchlistEnum.INITIATORQUANTITY_COL_INDEX:
                return Level1.GetInitiatorQty();
            case WatchlistEnum.INITIATORTYPE_COL_INDEX:
                return Level1.GetInitiatorType();
            case WatchlistEnum.LASTTRADEDTIME_COL_INDEX:
                return Level1.GetLastTradedTime();
            case WatchlistEnum.AUCTIONNUMBER_COL_INDEX:
                return Level1.GetAuctionNumber();
            case WatchlistEnum.AUCTIONSTATUS_COL_INDEX:
                return Level1.GetAuctionStatus();
            case WatchlistEnum.FIFTYTWOWEEKHIGHPRICE_COL_INDEX:
                return Level1.GetFiftyTwoWeekHighPrice();
            case WatchlistEnum.FIFTYTWOWEEKLOWPRICE_COL_INDEX:
                return Level1.GetFiftyTwoWeekLowPrice();
            case WatchlistEnum.NSEVALUE_COL_INDEX:
                return Level1.GetNSEValue();
            case WatchlistEnum.DPR_COL_INDEX:
                return Level1.GetDPRValue();
            case WatchlistEnum.CODE_COL_INDEX:
                return instrument.GetExtToken();
            case WatchlistEnum.INSTRUMENTTYPE_COL_INDEX:
                return instrument.GetExtInsType();
            case WatchlistEnum.MARKETTYPE_COL_INDEX:
                return instrument.GetExtMarketType();
            case WatchlistEnum.SYMBOL_COL_INDEX:
                return instrument.GetExtSymbol();
            case WatchlistEnum.SERIES_COL_INDEX:
                return instrument.GetExtSeries();
            case WatchlistEnum.EXCHANGETRADING_COL_INDEX:
                return instrument != null ? instrument.TradingExchange : Resources.getResource('ExchangeId.None');
            case WatchlistEnum.BIDSOURCE_COL_INDEX:
                return Level1.GetBidSourceName();
            case WatchlistEnum.ASKSOURCE_COL_INDEX:
                return Level1.GetAskSourceName();
            case WatchlistEnum.LASTSOURCE_COL_INDEX:
                return Level1.GetLastSourceName();
            case WatchlistEnum.LPP_COL_INDEX:
                return Level1.GetLPP();
            case WatchlistEnum.CURRENTFUNDINGRATE_COL_INDEX:
                return Level1.GetSpreadedFundingRateValue();
            case WatchlistEnum.NEXTFUNDINGSETTLEMENT_COL_INDEX:
                return Level1.GetNextFundingSettlement();
            default:
                return 0;
        }
    }
    GetCurrentAccount() {
        const WL = this._parent;
        return WL != null ? WL.get('account') : DataCache.getPrimaryAccount();
    }
    getColumnData(column) {
        const value = this.getColumnValue(column);
        let formattedValue = '';
        let groupByValue = '';
        const instrument = this.instrument;
        const Level1 = instrument.Level1;
        const sess = this.sessionSettings;
        const ShowLots = WDSettings.isQuantityInLots;
        const account = this.GetCurrentAccount();
        if (!Level1) {
            return new ColumnData(value, Resources.getResource('panel.watchlist.NA'));
        }
        switch (column) {
            case WatchlistEnum.INSTRUMENT_COL_INDEX:
                formattedValue = value ? value.toString() : '';
                break;
            case WatchlistEnum.LAST_COL_INDEX:
                formattedValue = Level1.getFormatPrice(value);
                break;
            case WatchlistEnum.LASTSIZE_COL_INDEX:
                formattedValue = Level1.StrLastSize(ShowLots);
                break;
            case WatchlistEnum.DATE_COL_INDEX:
                formattedValue = isNullOrUndefined(value)
                    ? Resources.getResource('panel.watchlist.NA')
                    : DateTimeUtils.FormatToDate(value);
                break;
            case WatchlistEnum.TIME_COL_INDEX:
                formattedValue = isNullOrUndefined(value)
                    ? Resources.getResource('panel.watchlist.NA')
                    : DateTimeUtils.FormatToTime(value);
                break;
            case WatchlistEnum.BID_COL_INDEX:
            case WatchlistEnum.ASK_COL_INDEX:
            case WatchlistEnum.OPEN_COL_INDEX:
            case WatchlistEnum.HIGH_COL_INDEX:
            case WatchlistEnum.LOW_COL_INDEX:
            case WatchlistEnum.PREVCLOSE_COL_INDEX:
                formattedValue = Level1.getFormatPrice(value);
                break;
            case WatchlistEnum.CHANGE_COL_INDEX:
                formattedValue = Level1.strChange(account);
                break;
            case WatchlistEnum.CHANGEPR_COL_INDEX:
                formattedValue = Level1.strChangePercent(account);
                break;
            case WatchlistEnum.EXPDATE_COL_INDEX:
                formattedValue = (value === null || value === void 0 ? void 0 : value.getTime()) > 0 ? DateTimeUtils.FormatToDate(value) : '';
                break;
            case WatchlistEnum.SPREAD_COL_INDEX:
                formattedValue = Level1.strSpread(account);
                break;
            case WatchlistEnum.SPREADPR_COL_INDEX:
                formattedValue =
                    instrument && !isNaN(value)
                        ? Level1.strSpreadPercent(account, value)
                        : Resources.getResource('panel.watchlist.NA');
                break;
            case WatchlistEnum.TYPE_COL_INDEX:
                if (instrument) {
                    formattedValue = instrument.getTypeString();
                    groupByValue = InstrumentUtils.getPutCallOptionsTypeStringLocalized(instrument);
                }
                break;
            case WatchlistEnum.INDICATIVEAUCTIONPRICE_COL_INDEX:
                formattedValue = Level1.strIndicativeAuctionPrice();
                break;
            case WatchlistEnum.OPENINTEREST_COL_INDEX:
                formattedValue = isValidNumber(value) ? value.toString() : StringUtils.NOT_AVAILABLE;
                break;
            case WatchlistEnum.SETTLEMENTPRICE_COL_INDEX:
                formattedValue = Level1.strSettlementPrice();
                break;
            case WatchlistEnum.PREVSETTLEMENTPRICE_COL_INDEX:
                formattedValue = Level1.strPrevSettlementPrice();
                break;
            case WatchlistEnum.VOLUME_COL_INDEX:
                formattedValue = VolumeFormatter.getVolumeFormat(value);
                break;
            case WatchlistEnum.MATCHVOLUME_COL_INDEX:
                formattedValue = Level1.strIndicativeAuctionVolume(ShowLots);
                break;
            case WatchlistEnum.REFERENCEPRICE_COL_INDEX:
                formattedValue = Level1.strReferencePrice;
                break;
            case WatchlistEnum.TRADEDVALUE_COL_INDEX:
                formattedValue = Level1.strTradedValue;
                break;
            case WatchlistEnum.OFFEXCHANGEVOLUME_COL_INDEX:
                formattedValue = Level1.strOffExchangeVolume(ShowLots);
                break;
            case WatchlistEnum.OFFEXCHANGEVALUE_COL_INDEX:
                formattedValue = Level1.strOffExchangeValue;
                break;
            case WatchlistEnum.NORMALMARKETSIZE_COL_INDEX:
                formattedValue = Level1.getNormalMarketSize;
                break;
            case WatchlistEnum.LIMITHIGH_COL_INDEX:
                formattedValue = instrument.Limits.HighLimitPriceByMeasureString(sess);
                break;
            case WatchlistEnum.LIMITLOW_COL_INDEX:
                formattedValue = instrument.Limits.LowLimitPriceByMeasureString(sess);
                break;
            case WatchlistEnum.CBAUCTIONSTART_COL_INDEX:
                formattedValue = Level1.strCbAuctionStart();
                break;
            case WatchlistEnum.ORDER_COL_INDEX:
            case WatchlistEnum.TRADE_COL_INDEX:
                formattedValue = value ? value.status : null;
                break;
            // nanFields not -1
            case WatchlistEnum.CLOSE_COL_INDEX:
            case WatchlistEnum.HIGH13_COL_INDEX:
            case WatchlistEnum.LOW13_COL_INDEX:
            case WatchlistEnum.HIGH26_COL_INDEX:
            case WatchlistEnum.LOW26_COL_INDEX:
            case WatchlistEnum.HIGH52_COL_INDEX:
            case WatchlistEnum.LOW52_COL_INDEX:
            case WatchlistEnum.AVGTRADEDPRICE_COL_INDEX:
            case WatchlistEnum.HIGHOPENINTEREST_COL_INDEX:
            case WatchlistEnum.LOWOPENINTEREST_COL_INDEX:
            case WatchlistEnum.INITIATORPRICE_COL_INDEX:
            case WatchlistEnum.FIFTYTWOWEEKHIGHPRICE_COL_INDEX:
            case WatchlistEnum.FIFTYTWOWEEKLOWPRICE_COL_INDEX:
            case WatchlistEnum.BIDSIZE_COL_INDEX:
            case WatchlistEnum.ASKSIZE_COL_INDEX:
                formattedValue = isNaN(value) || value === null ? StringUtils.NOT_AVAILABLE : value.toString();
                break;
            case WatchlistEnum.NSEVALUE_COL_INDEX:
                formattedValue =
                    Level1 != null
                        ? Level1.GetNSEFormatted(false, Resources.getResource('panel.watchlist.NA'))
                        : Resources.getResource('panel.watchlist.NA');
                break;
            case WatchlistEnum.CURRENTFUNDINGRATE_COL_INDEX:
                formattedValue =
                    Level1 != null
                        ? Level1.GetSpreadedFundingRateValueFormatted(account, Resources.getResource('panel.watchlist.NA'))
                        : Resources.getResource('panel.watchlist.NA');
                break;
            case WatchlistEnum.NEXTFUNDINGSETTLEMENT_COL_INDEX:
                formattedValue =
                    Level1 != null
                        ? Level1.StrNextFundingSettlement(Resources.getResource('panel.watchlist.NA'))
                        : Resources.getResource('panel.watchlist.NA');
                break;
            default:
                formattedValue =
                    isNullOrUndefined(value) || value === '' || value === -1
                        ? StringUtils.NOT_AVAILABLE
                        : value.toString();
        }
        if (!formattedValue && formattedValue !== '') {
            formattedValue = StringUtils.NOT_AVAILABLE;
        }
        return new ColumnData(value, formattedValue, groupByValue);
    }
    GetColumnParams(column) {
        const par = BaseInformer.columnsParams[column];
        par.Hidden = par.DefaultHidden;
        par.MenuIndex = this.GetMenuIndex(par.HeaderKey);
        const w = WATCHLIST_COLUMN_RULE.get(column);
        const columnAvailable = !isNullOrUndefined(w) ? DataCache.IsColumnAvailable(w.group, w.rule) : true;
        if (!columnAvailable) {
            par.Hidden = true;
        }
        if (Resources.isHidden(par.HeaderKey)) {
            par.Hidden = true;
        }
        if (column === WatchlistEnum.ITCHARTADVANCED_COL_INDEX) {
            const visible = this.isITChartAdvancedVisible();
            par.Hidden = !visible;
            par.Visible = visible;
        }
        return par;
    }
    GetCurrentInstrument() {
        return this.instrument;
    }
    changeSymbol(instDescr) {
        const dc = this.dataCache;
        if (this.instrument) {
            this.sendQuoteRequestSubscription(false);
            if (this.isDisplayed) {
                this.sendSubscription(false);
            }
            this.instrument.Level1.OnCbAuctionStartChanged.UnSubscribe(this.OnLastQuote, this);
            this.instrument.ExchangeSessionNameUpdated.UnSubscribe(this.OnExchangeSessionNameUpdated, this);
            this.instrument.RiskSettingsUpdated.UnSubscribe(this.OnInsRiskSettingsUpdated, this);
            this.instrument = null;
        }
        const interiorID = !isNullOrUndefined(instDescr)
            ? InstrumentDescriptionUtils.createInteriorId(instDescr)
            : null;
        this.instrument = dc.getInstrumentByName(interiorID);
        if (this.instrument) {
            this.sendQuoteRequestSubscription(true);
            if (this.isDisplayed) {
                this.sendSubscription(true);
            }
            this.ContractSize = this.instrument.LotSize;
            this.instrument.Level1.OnCbAuctionStartChanged.Subscribe(this.OnLastQuote, this);
            this.instrument.ExchangeSessionNameUpdated.Subscribe(this.OnExchangeSessionNameUpdated, this);
            this.instrument.RiskSettingsUpdated.Subscribe(this.OnInsRiskSettingsUpdated, this);
        }
        else {
            this.ContractSize = 0;
        }
    }
    sendSubscription(isSubscribe) {
        const ins = this.instrument;
        if (isNullOrUndefined(ins)) {
            return;
        }
        const quoteCache = this.dataCache.FQuoteCache;
        if (isSubscribe) {
            quoteCache.addListener(this.instrument, this, HistoryType.QUOTE_LEVEL1);
            quoteCache.addListener(this.instrument, this, HistoryType.QUOTE_TRADES);
        }
        else {
            quoteCache.removeListener(this.instrument, this, HistoryType.QUOTE_LEVEL1);
            quoteCache.removeListener(this.instrument, this, HistoryType.QUOTE_TRADES);
        }
    }
    sendQuoteRequestSubscription(isSubscribe) {
        const ins = this.instrument;
        if (isNullOrUndefined(ins)) {
            return;
        }
        const quoteCache = this.dataCache.FQuoteCache;
        if (isSubscribe) {
            quoteCache.addQuoteRequestListener(this.instrument, this);
        }
        else {
            quoteCache.removeQuoteRequestListener(this.instrument, this);
        }
    }
    newQuote(quote) {
        if (!quote) {
            return;
        }
        this.fireUpdate();
    }
    OnLastQuote() {
        if (!isNullOrUndefined(this.instrument)) {
            const message = this.instrument.GetLastQuote(QuoteValid.Last);
            if (message) {
                this.newQuote(message);
            }
        }
    }
    OnExchangeSessionNameUpdated() {
        this.fireUpdate();
    }
    Dispose() {
        this.changeSymbol(null);
        const p = this._parent;
        if (p) {
            this.QuickTableEditingInfoMapChanged.UnSubscribe(p.OnQuickTableEditingInfoMapChanged, p);
        }
    }
    OnInsRiskSettingsUpdated() {
        var _a, _b, _c;
        const ins = this.GetCurrentInstrument();
        if (isNullOrUndefined(ins)) {
            return;
        }
        const newInfo = this.buildSymbolEditingInfo(ins);
        const oldInfo = (_a = this.QuickTableEditingInfoMap[WatchlistEnum.INSTRUMENT_COL_INDEX]) !== null && _a !== void 0 ? _a : null;
        if (((_b = oldInfo === null || oldInfo === void 0 ? void 0 : oldInfo.ControlType) !== null && _b !== void 0 ? _b : null) === ((_c = newInfo === null || newInfo === void 0 ? void 0 : newInfo.ControlType) !== null && _c !== void 0 ? _c : null))
            return;
        this.QuickTableEditingInfoMap[WatchlistEnum.INSTRUMENT_COL_INDEX] = newInfo;
        this.QuickTableEditingInfoMapChanged.Raise(this.ItemId, newInfo); // обновился айтем, но нужная инфа не в нем, а в ячейке, потому подписали WatchList на обновление айтема и из WL организуем обновление полей у cell и перерисовку
    }
    NeedSortMenu() {
        return true;
    }
    GetMenuIndex(headerKey) {
        if (BaseInformer.ListMenuIndex.Contains(headerKey)) {
            return BaseInformer.ListMenuIndex.indexOf(headerKey);
        }
        else {
            return 1000;
        }
    }
    isITChartAdvancedVisible() {
        var _a, _b;
        const iTChartKey = Resources.getResourceLang('ITAdvancedChart.AllowKey', 'en');
        const iTChartValue = Resources.getResourceLang('ITAdvancedChart.AllowValue', 'en');
        if (!DataCache.ExternalLinksCache.getITChartAdvancedExtLink() || !iTChartKey || !iTChartValue) {
            return false;
        }
        const acc = DataCache.MainAccountNew;
        return ((_b = (_a = acc === null || acc === void 0 ? void 0 : acc.User) === null || _a === void 0 ? void 0 : _a.UserAdditionalInfo) === null || _b === void 0 ? void 0 : _b[iTChartKey]) === iTChartValue;
    }
    buildSymbolEditingInfo(ins) {
        if (isNullOrUndefined(ins) || !ins.HasDelay())
            return null;
        const info = new QuickTableEditingInfo(DynProperty.DELAYED_PICTURE_RIGHT_AND_TEXT);
        info.GetDataHandler = ins.GetDelayedIconTooltip.bind(ins);
        return info;
    }
}
BaseInformer.num = 0;
BaseInformer.columnsParams = [
    /* 0 */ new ColumnParams('panel.watchlist.Instrument', 100, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.watchlist.Company', 110, QTAlignment.Left, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.watchlist.Market', 40, QTAlignment.Left, TableTotalsVisibility.None, false, true, true),
    new ColumnParams('panel.watchlist.Last', 60, QTAlignment.Right, TableTotalsVisibility.None, true, false /*, beginGroupFlag: true, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.LastSize', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    /* 5 */ new ColumnParams('panel.watchlist.Date', 60, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Time', 60, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Main Volume', 60, QTAlignment.Right, TableTotalsVisibility.None, true, false, true /*, beginGroupFlag: true */),
    new ColumnParams('panel.watchlist.Bid', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.BidSize', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    /* 10 */ new ColumnParams('panel.watchlist.Ask', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.AskSize', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.Change', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.ChangePr', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.Open', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    /* 15 */ new ColumnParams('panel.watchlist.High', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.Low', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.PrevClose', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.ContractSize', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.ExpDate', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 20 */ new ColumnParams('panel.watchlist.Spread', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.SpreadPr', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.Descr', 100, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Type', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.watchlist.MarketCap', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, hidden: */, true),
    /* 25 */ new ColumnParams('panel.watchlist.noQuotes', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.IndicativeAuctionPrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, beginGroupFlag: true, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.CurrentSession', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false), // 27
    new ColumnParams('InstrumentDetailsPanel.TradingStatus', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false), // 28
    new ColumnParams('panel.watchlist.OpenPreMarket', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false, true),
    /* 30 */ new ColumnParams('panel.watchlist.ClosePostMarket', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.watchlist.Ticks', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.TicksPreMarket', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.watchlist.TicksPostMarket', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.watchlist.VolumePreMarket', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    /* 35 */ new ColumnParams('panel.watchlist.VolumePostMarket', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.watchlist.PreMarketChange%', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.watchlist.PreMarketChange', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.watchlist.ChangeFromOpen%', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.watchlist.ChangeFromOpen', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    /* 40 */ new ColumnParams('panel.watchlist.OpenInterest', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.SettlementPrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.PrevSettlementPrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Close', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.watchlist.ExchangeMarketData', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 45 */ new ColumnParams('panel.watchlist.Volume', 60, QTAlignment.Right, TableTotalsVisibility.None, true, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.Match Volume', 60, QTAlignment.Right, TableTotalsVisibility.None, false, false /*, emptyValue: -1.0 */),
    new ColumnParams('panel.watchlist.Comment', 60, QTAlignment.Left, TableTotalsVisibility.None, false, false, true),
    new ColumnParams('panel.watchlist.High13', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Low13', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 50 */ new ColumnParams('panel.watchlist.High26', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Low26', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.High52', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Low52', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.ReferencePrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 55 */ new ColumnParams('panel.watchlist.TradedValue', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.OffExchangeVolume', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.OffExchangeValue', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.NormalMarketSize', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Limit.High', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 60 */ new ColumnParams('panel.watchlist.Limit.Low', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.CBAuctionStart', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.AuctionEndTime', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.RemainingQty', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.RemainingQtySide', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 65 */ new ColumnParams('panel.watchlist.Order', 42, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Trade', 42, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.ITChartAdvanced', 42, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.watchlist.AvgTradedPrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.TotalBuyQuantity', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 70 */ new ColumnParams('panel.watchlist.TotalSellQuantity', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.HighOpenInterest', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.LowOpenInterest', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.InitiatorPrice', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.InitiatorQuantity', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 75 */ new ColumnParams('panel.watchlist.InitiatorType', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.LastTradedTime', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.AuctionNumber', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.AuctionStatus', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.FiftyTwoWeekHighPrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 80 */ new ColumnParams('panel.watchlist.FiftyTwoWeekLowPrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.NSEValue', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.DPR', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Code', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.InstrumentType', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 85 */ new ColumnParams('panel.watchlist.MarketType', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Symbol', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.Series', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.ExchangeTrading', 70, QTAlignment.Left, TableTotalsVisibility.None, false),
    new ColumnParams('panel.watchlist.BidSource', 70, QTAlignment.Left, TableTotalsVisibility.None, false),
    /* 90 */ new ColumnParams('panel.watchlist.AskSource', 70, QTAlignment.Left, TableTotalsVisibility.None, false),
    new ColumnParams('panel.watchlist.LastSource', 70, QTAlignment.Left, TableTotalsVisibility.None, false),
    new ColumnParams('panel.watchlist.LPP', 70, QTAlignment.Left, TableTotalsVisibility.None, false),
    new ColumnParams('panel.watchlist.CurrentFundingRate', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.watchlist.NextFundingSettlement', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false)
];
BaseInformer.ListMenuIndex = [
    //
    'panel.watchlist.Instrument',
    'panel.watchlist.Descr',
    'panel.watchlist.Type',
    'panel.watchlist.ExchangeMarketData',
    'panel.watchlist.ExchangeTrading',
    'panel.watchlist.ContractSize',
    'panel.watchlist.ExpDate',
    'panel.watchlist.noQuotes',
    'panel.watchlist.Date',
    'panel.watchlist.Time',
    'panel.watchlist.Last',
    'panel.watchlist.LastSize',
    'panel.watchlist.LastSource',
    'panel.watchlist.Bid',
    'panel.watchlist.BidSize',
    'panel.watchlist.BidSource',
    'panel.watchlist.Ask',
    'panel.watchlist.AskSize',
    'panel.watchlist.AskSource',
    'panel.watchlist.Spread',
    'panel.watchlist.SpreadPr',
    'panel.watchlist.Main Volume',
    'panel.watchlist.Change',
    'panel.watchlist.ChangePr',
    'panel.watchlist.Open',
    'panel.watchlist.High',
    'panel.watchlist.Low',
    'panel.watchlist.PrevClose',
    'panel.watchlist.OpenInterest',
    'panel.watchlist.Ticks',
    'panel.watchlist.Close',
    'panel.watchlist.IndicativeAuctionPrice',
    'panel.watchlist.Match Volume',
    'panel.watchlist.CurrentSession',
    'InstrumentDetailsPanel.TradingStatus',
    'panel.watchlist.Volume',
    'panel.watchlist.OpenPreMarket',
    'panel.watchlist.ClosePostMarket',
    'panel.watchlist.TicksPreMarket',
    'panel.watchlist.TicksPostMarket',
    'panel.watchlist.VolumePreMarket',
    'panel.watchlist.VolumePostMarket',
    'panel.watchlist.PreMarketChange%',
    'panel.watchlist.PreMarketChange',
    'panel.watchlist.ChangeFromOpen%',
    'panel.watchlist.ChangeFromOpen',
    'panel.watchlist.SettlementPrice',
    'panel.watchlist.PrevSettlementPrice',
    'panel.watchlist.Company',
    'panel.watchlist.Market',
    'panel.watchlist.MarketCap',
    'panel.watchlist.TradedValue',
    'panel.watchlist.OffExchangeVolume',
    'panel.watchlist.OffExchangeValue',
    'panel.watchlist.NormalMarketSize',
    'panel.watchlist.Limit.High',
    'panel.watchlist.Limit.Low',
    'panel.watchlist.CBAuctionStart',
    'panel.watchlist.AvgTradedPrice',
    'panel.watchlist.TotalBuyQuantity',
    'panel.watchlist.TotalSellQuantity',
    'panel.watchlist.HighOpenInterest',
    'panel.watchlist.LowOpenInterest',
    'panel.watchlist.InitiatorPrice',
    'panel.watchlist.InitiatorQuantity',
    'panel.watchlist.InitiatorType',
    'panel.watchlist.LastTradedTime',
    'panel.watchlist.AuctionNumber',
    'panel.watchlist.AuctionStatus',
    'panel.watchlist.FiftyTwoWeekHighPrice',
    'panel.watchlist.FiftyTwoWeekLowPrice',
    'panel.watchlist.NSEValue',
    'panel.watchlist.DPR',
    'panel.watchlist.Code',
    'panel.watchlist.InstrumentType',
    'panel.watchlist.MarketType',
    'panel.watchlist.Symbol',
    'panel.watchlist.Series'
];
export class Infor extends BaseInformer {
    constructor(instDescr, dataCache, sessionSettings, parent) {
        super(dataCache, sessionSettings);
        this.itemId = InstrumentDescriptionUtils.createInteriorId(instDescr);
        this.changeSymbol(instDescr);
        this.QuickTableEditingInfoMap[WatchlistEnum.INSTRUMENT_COL_INDEX] = this.buildSymbolEditingInfo(this.GetCurrentInstrument());
        this.QuickTableEditingInfoMap[WatchlistEnum.ORDER_COL_INDEX] = new QuickTableEditingInfo(DynProperty.ORDER_BUTTON);
        this.QuickTableEditingInfoMap[WatchlistEnum.TRADE_COL_INDEX] = new QuickTableEditingInfo(DynProperty.TRADE_BUTTON);
        this.QuickTableEditingInfoMap[WatchlistEnum.ITCHARTADVANCED_COL_INDEX] = new QuickTableEditingInfo(DynProperty.ITCHART_ADVANCED);
        this.QuickTableEditingInfoMapChanged = new CustomEvent(); // Raise произойдет когда у инструмента соотв. данному айтему обновятся RiskSettings, и в частности изменится QuoteDelay -> надо обновить QuickTableEditingInfo уже не у айтема (не только у него), а (главное обновить) у ячейки (см. public override OnInsRiskSettingsUpdated
        if (!isNullOrUndefined(parent)) {
            this._parent = parent; // in order to make UnSubscribe in item.Dispose(), we save ref to WatchList-caller - owner of this item here
            this.QuickTableEditingInfoMapChanged.Subscribe(parent.OnQuickTableEditingInfoMapChanged, parent);
        }
    }
}
